import React, { useState } from "react";
import PropTypes from "prop-types";
import { faCircleChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import { ButtonBase } from "@material-ui/core";

// Config
import colors from "src/config/colors";
import { panelNames } from "../../context/CasesPageProvider";
import useTranslate from "../../hooks/useTranslate";
import notFoundImg from "../../assets/icons/empty_state_img.svg";

// Components
import Button from "../../components/Button";
import CasePanel from "../../components/CasePanel";
import ReferenceNumberDialog from "../../components/ReferenceNumberDialog";
import Text from "../../components/Text/Text";
import CasesPageCreateCase from "./CasesPageCreateCase";

const useStyles = makeStyles(theme => ({
  boxContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    gap: 16
  },
  textWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    marginBottom: "48px",
    padding: 50
  },
  title: {
    fontSize: "40px",
    color: colors.riverBed
  },
  buttonWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  buttonBase: {
    border: "1px solid #D8DCE6",
    borderRadius: "4px",
    padding: theme.spacing(2),
    width: "700px",
    backgroundColor: "#fff",
    textAlign: "left",
    justifyContent: "space-between",
    fontSize: "15px",
    fontWeight: 600
  },
  icon: {
    color: "#E0E0E0"
  },
  or: {
    margin: "0 auto"
  },
  button: {
    width: "700px",
    borderRadius: "12px",
    textTransform: "uppercase"
  }
}));

const CasesPageContent = ({
  drafts,
  collaborations,
  submitted,
  pending,
  isSearching
}) => {
  const getText = useTranslate();
  const classes = useStyles();

  const hasDrafts = drafts.length > 0;
  const hasSubmitted = submitted.length > 0;
  const hasCollabs = collaborations.length > 0;
  const hasPending = pending.length > 0;

  const hasAnyContent = hasDrafts || hasSubmitted || hasCollabs || hasPending;

  if (!hasAnyContent) {
    if (isSearching) {
      return (
        <Box className={classes.boxContainer}>
          <Box
            component="img"
            src={notFoundImg}
            alt={getText("casesPage.search.emptyText")}
          />
          <Text text="casesPage.search.emptyText" type="large" />
        </Box>
      );
    }
    return <CasesPageCreateCase />;
  }

  return (
    <>
      {hasDrafts && (
        <CasePanel
          name={panelNames.DRAFT}
          data={drafts}
          title={getText("common.status.draft")}
        />
      )}
      {hasSubmitted && (
        <CasePanel
          name={panelNames.SUBMITTED}
          data={submitted}
          title={getText("common.status.submitted")}
        />
      )}
      {(hasCollabs || hasPending) && (
        <CasePanel
          name={panelNames.COLLAB}
          data={collaborations}
          pending={pending}
          title={getText("casesPage.Collaborations")}
          isCollab
        />
      )}
    </>
  );
};

CasesPageContent.propTypes = {
  drafts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  collaborations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  submitted: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pending: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isSearching: PropTypes.bool.isRequired
};

export default React.memo(CasesPageContent);
